import { Injectable } from '@angular/core';
import { WorkspaceRepository } from '@database/central';
import { DataRepository, RxdbIdProvider, TableRepository, WorkspaceDataImporterService } from '@database/workspace';
import { DataModel, ErrorCodes, PrimaryKeyValueModel, TableModel, ToastDuration, ToastService } from '@shared';
import { LoggingService } from '@shared/logging';
import { catchError, combineLatest, firstValueFrom, map, switchMap } from 'rxjs';
import { BusinessLayerService } from '../../services';
import { QueryParamsProvider } from '../../../routing/services/query-params.provider';
import { CheckoutSanitizer } from './checkout.sanitizer';

@Injectable()
export class CheckoutService {
	public constructor(
		private workspaceRepository: WorkspaceRepository,
		private dataRepository: DataRepository,
		private loggingService: LoggingService,
		private tableRepository: TableRepository,
		private businessLayerService: BusinessLayerService,
		private queryParamsProvider: QueryParamsProvider,
		private workspaceDataImporterService: WorkspaceDataImporterService,
		private rxdbIdProvider: RxdbIdProvider,
		private toastService: ToastService,
		private checkoutSanitizer: CheckoutSanitizer
	) {}

	public async updateRecords(
		workfileId: string,
		tableName: string,
		entryId: number,
		workfileRecords: DataModel[]
	): Promise<void> {
		const recordWithoutExcludedItems: DataModel[] = this.checkoutSanitizer.removeDataRecordsStub(workfileRecords);

		if (recordWithoutExcludedItems.length === 0) {
			throw new Error(ErrorCodes.CheckoutNoRecordToUpdate);
		}

		let parentRecordToSaveLowerCase: DataModel = {};
		const childRecordsToSaveLowerCase: DataModel[] = [];
		recordWithoutExcludedItems.forEach((item: DataModel) => {
			const recordToSave: DataModel = this.checkoutSanitizer.toLowerCase(item);
			const isParentRecord: boolean = recordToSave.table_name === tableName;

			if (isParentRecord) {
				parentRecordToSaveLowerCase = recordToSave;
			} else {
				childRecordsToSaveLowerCase.push(recordToSave);
			}
		});

		try {
			const tablePrimaryKeys = await firstValueFrom(
				this.workspaceRepository.getEntityPrimaryKey$(workfileId, tableName)
			);

			// update parent record
			await this.dataRepository.updateOne(parentRecordToSaveLowerCase, tableName, entryId, tablePrimaryKeys);

			// remove child table records
			await firstValueFrom(
				this.tableRepository.getTableChildIdsByName$(tableName).pipe(
					switchMap((childTableIds: number[] | null) => {
						if (childTableIds) {
							return this.businessLayerService
								.getPrimaryKeyAndValue$(
									this.queryParamsProvider.workfileId,
									this.queryParamsProvider.projectNumber,
									tableName,
									entryId
								)
								.pipe(
									switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
										combineLatest([
											this.tableRepository
												.getChildTablesMetadataByIds$(childTableIds)
												.pipe(
													map((childTables: TableModel[]) =>
														childTables.map((childTable: TableModel) =>
															childTable.table_name.toLowerCase()
														)
													)
												),
											this.tableRepository.getStandardTable$(tableName)
										]).pipe(
											switchMap(
												async ([childTablesName, standardTableName]: [
													string[],
													string | null
												]) => {
													if (standardTableName) {
														childTablesName.push(standardTableName.toLowerCase());
													}

													return childTablesName.forEach(async (item: string) => {
														await this.dataRepository.deleteRecords(
															item,
															this.queryParamsProvider.projectNumber,
															primaryKeyValue
														);
													});
												}
											),
											catchError((error) => {
												this.loggingService.error(ErrorCodes.CheckoutError, error.errorMessage);
												throw new Error(error);
											})
										)
									)
								);
						}
					})
				)
			);

			// insert child table records
			const lastRxdbId = await this.rxdbIdProvider.getCurrentRxdbId(workfileId);
			await this.workspaceDataImporterService.importData(childRecordsToSaveLowerCase, workfileId, lastRxdbId);
		} catch (error) {
			await this.toastService.presentError(error.errorMessage, ToastDuration.Slow);
			this.loggingService.error(ErrorCodes.CheckoutError, error.errorMessage);
			throw new Error(error);
		}
	}
}
